/**
 * BOM (Bill of Materials) Page
 *
 * Main page for managing BOMs (manufacturing recipes)
 * Features:
 * - List view with search and filtering
 * - Create/Edit BOM functionality
 * - Version management
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Plus,
  RefreshCw,
  Search,
  Loader2,
  FileText,
  Edit,
  Trash2,
  Eye,
  Copy,
  X,
} from 'lucide-react';
import Layout from '../components/Layout';
import authService from '../services/authService';
import bomService from '../services/bomService';

function BomPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [boms, setBoms] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize] = useState(20);

  // Read state from URL query params
  const page = parseInt(searchParams.get('page') || '1', 10);
  const searchTerm = searchParams.get('search') || '';
  const statusFilter = searchParams.get('status') || '';
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Load user info on mount
  useEffect(() => {
    const currentUser = authService.getCurrentUser();
    setUser(currentUser);
  }, []);

  // Load BOMs
  const loadBoms = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page,
        page_size: pageSize,
      };

      if (searchTerm) {
        params.search = searchTerm;
      }

      if (statusFilter) {
        params.status = statusFilter;
      }

      const response = await bomService.getBoms(params);
      setBoms(response.items || []);
      setTotalItems(response.total || 0);
    } catch (err) {
      console.error('Failed to load BOMs:', err);
      setError('Failed to load BOMs. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchTerm, statusFilter]);

  useEffect(() => {
    loadBoms();
  }, [loadBoms, refreshTrigger]);

  const handleRefresh = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  const updateSearchParams = (updates) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === '' || value === null || value === undefined) {
        newParams.delete(key);
      } else {
        newParams.set(key, String(value));
      }
    });
    setSearchParams(newParams, { replace: true });
  };

  const handleSearch = (e) => {
    updateSearchParams({ search: e.target.value, page: '1' });
  };

  const handleStatusFilter = (value) => {
    updateSearchParams({ status: value === 'all' ? '' : value, page: '1' });
  };

  const handlePageChange = (newPage) => {
    updateSearchParams({ page: String(newPage) });
  };

  const handleCreateBom = () => {
    navigate('/bom/new');
  };

  const handleViewBom = (bomId) => {
    navigate(`/bom/${bomId}`);
  };

  const handleEditBom = (bomId) => {
    navigate(`/bom/${bomId}/edit`);
  };

  const handleCreateVersion = async (bomId) => {
    try {
      setLoading(true);
      await bomService.createNewVersion(bomId, {});
      handleRefresh();
    } catch (err) {
      setError(`Failed to create new version: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBom = async (bomId) => {
    if (!window.confirm('确定要作废此BOM吗？')) {
      return;
    }

    try {
      setLoading(true);
      await bomService.deleteBom(bomId);
      handleRefresh();
    } catch (err) {
      setError(`Failed to delete BOM: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: 'default',
      draft: 'secondary',
      obsolete: 'destructive',
    };
    const labels = {
      active: '有效',
      draft: '草稿',
      obsolete: '已作废',
    };
    return (
      <Badge variant={variants[status] || 'secondary'}>
        {labels[status] || status}
      </Badge>
    );
  };

  // Check user permissions
  const canCreate = user && ['admin', 'warehouse_manager', 'engineer'].includes(user.role);
  const canEdit = user && ['admin', 'warehouse_manager', 'engineer'].includes(user.role);

  const totalPages = Math.ceil(totalItems / pageSize);

  return (
    <Layout>
      <div className="container mx-auto py-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">物料清单</h1>
            <p className="text-muted-foreground">
              管理生产配方和物料需求
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleRefresh} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              刷新
            </Button>
            {canCreate && (
              <Button onClick={handleCreateBom}>
                <Plus className="h-4 w-4 mr-2" />
                新建BOM
              </Button>
            )}
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="destructive">
            <AlertDescription className="flex items-center justify-between">
              {error}
              <Button variant="ghost" size="sm" onClick={() => setError(null)}>
                <X className="h-4 w-4" />
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="搜索BOM编号、产品代码或名称..."
                    value={searchTerm}
                    onChange={handleSearch}
                    className="pl-10"
                  />
                </div>
              </div>
              <Select value={statusFilter || 'all'} onValueChange={handleStatusFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="按状态筛选" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">全部状态</SelectItem>
                  <SelectItem value="active">有效</SelectItem>
                  <SelectItem value="draft">草稿</SelectItem>
                  <SelectItem value="obsolete">已作废</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* BOM Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              BOM列表 ({totalItems})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center items-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : boms.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                未找到BOM。{canCreate && '点击"新建BOM"添加。'}
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>BOM编号</TableHead>
                      <TableHead>产品</TableHead>
                      <TableHead>版本</TableHead>
                      <TableHead>物料数</TableHead>
                      <TableHead>预估成本</TableHead>
                      <TableHead>状态</TableHead>
                      <TableHead>默认</TableHead>
                      <TableHead className="text-right">操作</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {boms.map((bom) => (
                      <TableRow key={bom._id}>
                        <TableCell className="font-mono">{bom.bom_id}</TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">{bom.finished_product_code}</div>
                            <div className="text-sm text-muted-foreground">
                              {bom.finished_product_name}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>V{bom.version}</TableCell>
                        <TableCell>{bom.materials?.length || 0}项</TableCell>
                        <TableCell>
                          {bom.estimated_total_cost
                            ? `$${bom.estimated_total_cost.toFixed(2)}`
                            : '-'}
                        </TableCell>
                        <TableCell>{getStatusBadge(bom.status)}</TableCell>
                        <TableCell>
                          {bom.is_default && (
                            <Badge variant="outline">默认</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewBom(bom._id)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            {canEdit && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEditBom(bom._id)}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleCreateVersion(bom._id)}
                                  title="创建新版本"
                                >
                                  <Copy className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteBom(bom._id)}
                                  className="text-destructive hover:text-destructive"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-between items-center mt-4">
                    <div className="text-sm text-muted-foreground">
                      第 {page} 页 / 共 {totalPages} 页（共 {totalItems} 项）
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(Math.max(1, page - 1))}
                        disabled={page === 1}
                      >
                        上一页
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(Math.min(totalPages, page + 1))}
                        disabled={page === totalPages}
                      >
                        下一页
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}

export default BomPage;
